import { stringify } from "query-string";

import {
  FontIcon,
  FontWeights,
  ILinkStyles,
  ITextStyles,
  Link,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { SystemNoticeType } from "@shared-types/practice/system-notice-type.type.ts";
import { SystemNotice } from "@shared-types/practice/system-notice.interface.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { PatientCardIds } from "../types/patient-card-ids.enum.ts";
import { SystemNoticeTypeLabel } from "./SystemNotice.types.ts";

interface SideSystemNoticeTableRowProps {
  systemNotice: SystemNotice;
  patientId: string;
}

const linkStyles: ILinkStyles = {
  root: {
    flexShrink: 0
  }
};

export const SideSystemNoticeTableRow: React.FC<
  SideSystemNoticeTableRowProps
> = ({ systemNotice, patientId }) => {
  const { palette, semanticColors } = useTheme();
  const {
    routing,
    practice: {
      ui: { showEditContact }
    },
    core
  } = useStores();

  const commentStyle: ITextStyles = { root: { wordBreak: "break-word" } };

  const showActions =
    systemNotice.type !== SystemNoticeType.interpreter &&
    core.hasPermissions(Permission.PaymentCreate);

  return (
    <Stack
      horizontal
      verticalAlign="center"
      tokens={{ childrenGap: 4 }}
      styles={{ root: { minHeight: 32 } }}
    >
      <Stack.Item
        grow
        styles={{
          root: {
            color: palette.themeDarker,
            fontWeight: FontWeights.semibold
          }
        }}
      >
        <Stack
          horizontal
          tokens={{ childrenGap: 4 }}
          horizontalAlign="space-between"
        >
          <Stack horizontal tokens={{ childrenGap: 4 }}>
            <FontIcon
              iconName="Warning"
              styles={{
                root: { color: semanticColors.severeWarningIcon, marginTop: 2 }
              }}
            />
            <span>{SystemNoticeTypeLabel[systemNotice.type]}</span>
          </Stack>
          {showActions && (
            <>
              {systemNotice.type === SystemNoticeType.account &&
                systemNotice.owingTotal && (
                  <Link
                    styles={linkStyles}
                    onClick={() =>
                      routing.pushWithFromQuery({
                        pathname: routes.accounts.allocations.new.pattern,
                        search: stringify({ accountId: patientId })
                      })
                    }
                  >
                    Pay account
                  </Link>
                )}
              {systemNotice.type === SystemNoticeType.cardExpired && (
                <Link
                  styles={linkStyles}
                  onClick={() =>
                    showEditContact(
                      PatientCardIds.patientEntitlements,
                      patientId
                    )
                  }
                >
                  Update
                </Link>
              )}
            </>
          )}
        </Stack>

        {systemNotice.type === SystemNoticeType.account ? (
          <Stack styles={{ root: { paddingLeft: 18 } }}>
            {systemNotice.owingTotal && (
              <Text styles={commentStyle}>
                {"Owing "}
                <span style={{ fontWeight: FontWeights.semibold }}>
                  {systemNotice.owingTotal}
                </span>
              </Text>
            )}
            {systemNotice.unallocatedCredit && (
              <Text styles={commentStyle}>
                {"Credit "}
                <span style={{ fontWeight: FontWeights.semibold }}>
                  {systemNotice.unallocatedCredit}
                </span>
              </Text>
            )}
          </Stack>
        ) : (
          <Text styles={commentStyle}>{` - ${systemNotice.comment}`}</Text>
        )}
      </Stack.Item>
    </Stack>
  );
};
